import React, { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom/dist";
import Table from "../../core/pagination/datatable";
import { Download, PlusCircle } from "feather-icons-react/build/IconComponents";
import { ErrorToaster, SuccessToaster } from "../components/toaster/Toaster";
import InvoiceServices from "../../services/Invoice";
import InvoiceForm from "../../core/modals/invoice/invoiceForm";
import { cleanObject, Debounce, getDate } from "../../utils";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import Swal from "sweetalert2";
import { deleteAlert } from "../components/sweetAlert";
import { Tooltip } from "antd";

const Invoices = () => {

  const routes = all_routes;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { userData } = useSelector((state) => {
    return {
      userData: state.user_detail
    }
  });

  // *For Loader
  const [loader, setLoader] = useState(false);

  const [validationLoading, setValidationLoading] = useState(false);
  const [submittingLoading, setSubmittingLoading] = useState(false);

  // *For Invoices
  const [invoices, setInvoices] = useState([]);

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageLimit = parseInt(searchParams.get("limit") || "10", 10);
  const searchText = searchParams.get("search") || "";

  // *For Get Invoices
  const getInvoices = async (page = currentPage, limit = pageLimit) => {
    setLoader(true)
    try {
      const params = {
        ...Object.fromEntries(searchParams.entries()),
        page,
        limit
      }
      const { data } = await InvoiceServices.getInvoices(params)
      setInvoices(data?.invoices)
      setTotalCount(data?.pagination?.total)
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setLoader(false)
    }
  }

  // *For Delete Invoice
  const deleteInvoice = async (id) => {
    try {
      const isConfirmed = await deleteAlert()

      if (isConfirmed) {
        const { message } = await InvoiceServices.deleteInvoice(id);
        getInvoices();

        Swal.fire({
          title: "Deleted!",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        Swal.close();
      }
    } catch (error) {
      ErrorToaster(error)
    }
  }

  // *For Validate Invoice
  const validateInvoice = async (id) => {
    setValidationLoading(true)
    try {
      const obj = {
        invoice_id: id
      }
      const { message } = await InvoiceServices.validateInvoice(obj)
      // SuccessToaster(message)
      Swal.fire({
        title: "Validated!",
        text: message,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      ErrorToaster(error)
    } finally {
      getInvoices()
      setValidationLoading(false)
    }
  }

  // *For Submit Invoice
  const submitInvoice = async (id) => {
    setSubmittingLoading(true)
    try {
      const obj = {
        invoice_id: id
      }
      const { message } = await InvoiceServices.submitInvoice(obj)
      // SuccessToaster(message)
      Swal.fire({
        title: "Submitted!",
        text: message,
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      ErrorToaster(error)
    } finally {
      getInvoices()
      setSubmittingLoading(false)
    }
  }

  // *For Handle Pagination and Filters
  const handlePaginationFilter = (data) => {
    try {
      const obj = {
        ...data,
      }
      const updatedParams = {
        ...Object.fromEntries(searchParams.entries()),
        page: 1,
        ...cleanObject(obj)
      };
      Debounce(() => setSearchParams(updatedParams));
    } catch (error) {
      console.log(' handlePaginationFilter => error:', error)
    }
  }

  const columns = [
    {
      title: "Invoice Ref",
      dataIndex: "invoice_ref_no",
      render: (text, record) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      render: (text) => (
        <span>
          {getDate(text)}
        </span>
      ),
    },
    {
      title: "Invoice Type",
      dataIndex: "invoice_type",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer Name",
      dataIndex: "buyer_business_name",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer NTN/CNIC",
      dataIndex: "buyer_ntn_cnic",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer Province",
      dataIndex: "buyer_province",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Buyer Address",
      dataIndex: "buyer_address",
      render: (text) => (
        <div style={{ width: '200px', whiteSpace: 'break-spaces' }}>
          {text || '-'}
        </div>
      ),
    },
    {
      title: "Seller Name",
      dataIndex: "seller_business_name",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Seller NTN/CNIC",
      dataIndex: "seller_ntn_cnic",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Seller Province",
      dataIndex: "seller_province",
      render: (text) => (
        <span>
          {text || '-'}
        </span>
      ),
    },
    {
      title: "Seller Address",
      dataIndex: "seller_address",
      render: (text) => (
        <div style={{ width: '200px', whiteSpace: 'break-spaces' }}>
          {text || '-'}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        const statusColor = text === "PENDING" ? "badge-linegray" : text === "SUBMITTED" ? "badge-linesuccess" : text === "VALID" ? "badge-linewarning" : "badge-linedanger"
        return (
          <span
            className={`badge ${statusColor} text-capitalize`}
          >
            {text}
          </span>
        )
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text) => (
        <div style={{ width: '250px', whiteSpace: 'break-spaces' }}>
          {text || '-'}
        </div>
      ),
    },
    {
      title: "FBR Invoice No",
      dataIndex: "fbr_invoice_number",
      render: (text) => (
        <div>
          {text ? `FBR # ${text}` : '-'}
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => (
        <div className="action-table-data">
          {/* {record?.fbr_invoice_number && */}
          <button type="button" className="btn btn-sm btn-primary btn-wave waves-effect waves-light me-2"
            // disabled={!record?.fbr_invoice_number}
            onClick={() => navigate(`${routes?.viewInvoice}/${record?.id}`)}
          >
            View Invoice
          </button>
          {/* } */}
          {record?.status !== 'SUBMITTED' && record?.status !== 'VALID' &&
            <button type="button" className="btn btn-sm btn-warning btn-wave waves-effect waves-light me-2"
              disabled={validationLoading}
              onClick={() => validateInvoice(record?.id)}
            >
              Validate
            </button>
          }
          {record?.status === 'VALID' &&
            <button type="button" className="btn btn-sm btn-success btn-wave waves-effect waves-light me-2"
              disabled={submittingLoading}
              onClick={() => submitInvoice(record?.id)}
            >
              Submit
            </button>
          }
          {record?.status !== 'SUBMITTED' && record?.status !== 'VALID' &&
            <button type="button" className="btn btn-sm btn-info btn-wave waves-effect waves-light me-2"
              disabled={record?.status === 'SUBMITTED'}
              onClick={() => navigate(`${routes?.editInvoice}/${record?.id}`)}
            >
              Edit
            </button>
          }
          {record?.status !== 'SUBMITTED' && record?.status !== 'VALID' &&
            <button type="button" className="btn btn-sm btn-danger btn-wave waves-effect waves-light me-2"
              disabled={record?.status === 'SUBMITTED'}
              onClick={() => deleteInvoice(record?.id)}
            >
              Delete
            </button>
          }
        </div>
      ),
    },
  ];

  useEffect(() => {
    getInvoices()
  }, [searchParams]);

  return (
    <Fragment>

      <InvoiceForm getData={() => getInvoices()} />

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Invoice List</h4>
                <h6>Manage your invoices</h6>
              </div>
            </div>
            {userData?.role === 'user' &&
              <Fragment>
                <div className="page-btn">
                  <Link
                    to={routes.addInvoice}
                    className="btn btn-added"
                  >
                    <PlusCircle className="me-2" />
                    Add Invoice
                  </Link>
                </div>
                <div className="page-btn">
                  <Link
                    to="#"
                    className="btn btn-added btn-warning"
                    data-bs-toggle="modal"
                    data-bs-target="#open-modal"
                  >
                    <Download className="me-2" />
                    Import Invoice
                  </Link>
                </div>
              </Fragment>
            }
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      defaultValue={searchText}
                      placeholder="Search by invoice ref"
                      className="form-control form-control-sm formsearch"
                      onChange={(e) => handlePaginationFilter({ search: e.target.value })}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  loading={loader}
                  columns={columns}
                  dataSource={invoices}
                  totalCount={totalCount}
                  pageSize={pageLimit}
                  currentPage={currentPage}
                  onChange={(page, pageSize) => handlePaginationFilter({ page: page, limit: pageSize })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Invoices;
